import { defaultSchema } from "rehype-sanitize";

/**
 * Custom sanitization schema that allows safe HTML tags while blocking dangerous ones.
 * Extends the default rehype-sanitize schema to include common formatting tags.
 *
 * This schema is used across all markdown rendering components to ensure consistent
 * HTML sanitization while allowing safe tags like <br>, <strong>, <p>, etc.
 *
 * @returns Sanitization schema for use with rehype-sanitize
 *
 * @example
 * ```tsx
 * import { getSanitizationSchema } from './markdown-sanitization';
 *
 * <ReactMarkdown
 *   rehypePlugins={[
 *     rehypeRaw,
 *     [rehypeSanitize, getSanitizationSchema()],
 *   ]}
 * >
 *   {content}
 * </ReactMarkdown>
 * ```
 */
export const getSanitizationSchema = () => {
  // Deep clone the default schema using JSON
  const schema = JSON.parse(JSON.stringify(defaultSchema));

  // Add safe HTML tags to allowed tagNames
  const additionalTags = [
    "br",
    "strong",
    "b",
    "em",
    "i",
    "u",
    "mark",
    "small",
    "del",
    "ins",
    "sub",
    "sup",
    "abbr",
    "cite",
    "kbd",
    "samp",
    "var",
    "span",
    "div",
    "p",
  ];

  if (!schema.tagNames) {
    schema.tagNames = [];
  }
  schema.tagNames = [...schema.tagNames, ...additionalTags];

  // Allow className and class attributes on all elements
  if (!schema.attributes) {
    schema.attributes = {};
  }
  if (!schema.attributes["*"]) {
    schema.attributes["*"] = [];
  }
  schema.attributes["*"] = [...schema.attributes["*"], "className", "class"];

  // Ensure br tag has no restrictions
  if (!schema.attributes["br"]) {
    schema.attributes["br"] = [];
  }

  return schema;
};

/**
 * Memoized version of the sanitization schema.
 * Created once on module load to avoid recreating the schema on every render.
 */
export const sanitizationSchema = getSanitizationSchema();
